<script setup lang="ts">
import { TITRE_SECTION_APPLE } from "~/utils/titresApple";
/**
 * Tableau comparatif Donoor vs HelloAsso — charte « apple » (Karim 2026-10-18) :
 * logos réels, colonne Donoor mise en avant (bloc blanc + badge « Recommandé »),
 * cellules qui acceptent soit un booléen (✓ / ✗) soit du texte.
 *
 * Chaque ligne : { label, donoor, helloasso } où donoor/helloasso sont
 * `boolean` (rendu ✓/✗) ou `string` (rendu texte). Le CTA final pointe vers
 * l'inscription en conservant l'attribution Ads (useRegisterLink).
 */
interface Row {
  label: string;
  donoor: boolean | string;
  helloasso: boolean | string;
}

const props = withDefaults(
  defineProps<{
    rows: Row[];
    title?: string;
    titleHighlight?: string;
    subtitle?: string;
    /** Retire le fond gris (#fafcff) de la section. */
    transparentBg?: boolean;
    /** Nom du concurrent affiché en en-tête de la 2ᵉ colonne (défaut HelloAsso). */
    competitorName?: string;
    /**
     * Logo SVG du concurrent (défaut : logo HelloAsso). Si absent, on retombe
     * sur le nom du concurrent en texte — utile quand aucun logo n'existe.
     */
    competitorLogo?: string | null;
  }>(),
  {
    title: "Donoor vs HelloAsso",
    // Replis traduits plus bas (titleHighlightText, subtitleText).
    titleHighlight: undefined,
    subtitle: undefined,
    competitorName: "HelloAsso",
    competitorLogo: "/svg/helloasso-logo.svg",
  },
);

const { t } = useI18n({ useScope: "local" });

const titleHighlightText = computed(() => props.titleHighlight ?? t('comparatif.titreHighlight'));
const subtitleText = computed(() => props.subtitle ?? t('comparatif.sousTitre'));

const registerLink = useRegisterLink();
const localePath = useLocalePath();

// « Fonds direct sur le compte de l'asso » = argument n°1 de Donoor : toujours
// en première ligne du tableau (matche « fonds… » ou « où arrivent les dons »).
const orderedRows = computed(() => {
  const list = [...props.rows];
  const idx = list.findIndex((r) => /fonds|arrivent les dons/i.test(r.label));
  if (idx > 0) {
    const [row] = list.splice(idx, 1);
    if (row) list.unshift(row);
  }
  return list;
});
</script>

<template>
  <section class="w-full px-4 py-20 sm:px-8 lg:py-28" :class="transparentBg ? '' : 'bg-[#f5f5f7]'">
    <div class="mx-auto max-w-[920px]">
      <!-- Titre -->
      <div class="mx-auto mb-12 max-w-[760px] text-center">
        <h2 class="pa-reveal mb-5 text-[32px] text-[#1d1d1f] [text-wrap:balance] sm:text-[48px]" :style="TITRE_SECTION_APPLE">
          {{ title }}
          <span class="text-[#86868b]">{{ titleHighlightText }}</span>
        </h2>
        <p class="pa-reveal mx-auto max-w-[640px] text-[17px] font-medium leading-[1.55] text-[#424245] sm:text-[19px]">{{ subtitleText }}</p>
      </div>

      <!-- Tableau : Donoor mis en avant (colonne blanche sur fond gris) -->
      <div class="pa-reveal overflow-x-auto rounded-[28px] p-2 sm:p-3" :class="transparentBg ? 'bg-[#f5f5f7]' : 'bg-white'">
        <div class="min-w-[520px]">
          <div class="grid grid-cols-[1.5fr_1fr_1fr]">
            <div class="p-4 sm:p-5" />
            <div class="flex items-center justify-center p-4 sm:p-5">
              <img v-if="competitorLogo" :src="competitorLogo" :alt="competitorName" class="h-6 w-auto opacity-60 grayscale sm:h-7" />
              <span v-else class="text-[15px] font-semibold text-[#6e6e73] sm:text-[17px]">{{ competitorName }}</span>
            </div>
            <div class="relative flex flex-col items-center justify-center gap-2 rounded-t-[20px] p-4 pt-8 sm:p-5 sm:pt-9" :class="transparentBg ? 'bg-white' : 'bg-[#f5f5f7]'">
              <span class="absolute top-2.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-(--color-main) px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                {{ t("comparatif.recommande") }}
              </span>
              <img src="/svg/donoor-logo.svg" alt="Donoor" class="h-6 w-auto sm:h-7" width="200" height="52" />
            </div>
          </div>

          <div v-for="row in orderedRows" :key="row.label" class="grid grid-cols-[1.5fr_1fr_1fr] items-stretch">
            <div class="flex items-center border-t p-4 text-[14px] font-semibold text-[#1d1d1f] sm:p-5 sm:text-[15px]" :class="transparentBg ? 'border-[#d2d2d7]' : 'border-[#e8e8ed]'">
              {{ row.label }}
            </div>
            <div class="flex items-center justify-center border-t p-4 text-center sm:p-5" :class="transparentBg ? 'border-[#d2d2d7]' : 'border-[#e8e8ed]'">
              <template v-if="typeof row.helloasso === 'boolean'">
                <UIcon v-if="row.helloasso" name="i-lucide-check" class="size-5 text-[#86868b]" />
                <UIcon v-else name="i-lucide-x" class="size-5 text-[#c7c7cc]" />
              </template>
              <span v-else class="text-[13px] leading-snug text-[#6e6e73] sm:text-[14px]">{{ row.helloasso }}</span>
            </div>
            <div class="flex items-center justify-center border-t border-[#e8e8ed] p-4 text-center sm:p-5" :class="transparentBg ? 'bg-white' : 'bg-[#f5f5f7]'">
              <span v-if="typeof row.donoor === 'boolean'" class="flex size-6 items-center justify-center rounded-full bg-(--color-main)">
                <UIcon name="i-lucide-check" class="size-4 text-white" />
              </span>
              <span v-else class="text-[13px] font-semibold leading-snug text-[#1d1d1f] sm:text-[14px]">{{ row.donoor }}</span>
            </div>
          </div>

          <!-- Pied : bouton sous la colonne Donoor -->
          <div class="grid grid-cols-[1.5fr_1fr_1fr]">
            <div />
            <div />
            <div class="flex items-center justify-center rounded-b-[20px] p-4 sm:p-5" :class="transparentBg ? 'bg-white' : 'bg-[#f5f5f7]'">
              <BoutonApple v-register-cta="'comparatif'" :to="registerLink">{{ t("comparatif.choisir") }}</BoutonApple>
            </div>
          </div>
        </div>
      </div>

      <i18n-t keypath="comparatif.miseAJour" tag="p" scope="parent" class="pa-reveal mx-auto mt-6 max-w-[760px] text-center text-[14px] leading-relaxed text-[#6e6e73]">
        <template #lien>
          <NuxtLink :to="localePath({ path: '/comparateurs' })" class="font-semibold text-(--color-main) hover:underline">{{ t("comparatif.lien") }}</NuxtLink>
        </template>
      </i18n-t>
    </div>
  </section>
</template>

<i18n lang="json" src="../../../i18n/pages/components/shared/ComparatifDonoorHelloasso.json"></i18n>
